import type { PDFSplitResult } from "@pdfking/renderer-api"
import { PDFConvert, PDFSplit } from "@pdfking/renderer-default"
import { Peek, Range } from "@pdfking/tfunc"

import { GenerateFile } from "../../files/GenerateFile"
import { RequestBlob } from "../../files/RequestBlob"
import { ConvertRectangle } from "../../geometry/ConvertRectangle"
import { FileContentType } from "../../methods/FileContentType"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../../types/engine/PdfEngineOptions"
import { ContentType } from "../../types/files/ContentType"
import type { VirtualFile } from "../../types/files/VirtualFile"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskSplitOptions } from "../../types/tasks/options/TaskSplitOptions"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"

type SplitPagesResult = {
  page_count: number
} & PDFSplitResult

const SplitPages = async function* (
  engine: PdfEngine,
  options: PdfEngineOptions,
  file: VirtualFile,
  start_page: number,
  end_page: number,
  emit: TrackingCallback
): AsyncGenerator<SplitPagesResult> {
  const { stream, size } = await RequestBlob(engine, file, emit)
  const type = FileContentType(file)

  switch (type) {
    case ContentType.PDF:
      let page_count = 0
      for await (const result of PDFSplit({ stream, size, type }, total => {
        page_count = total
        return Range(start_page, Math.min(end_page, total))
      })) {
        yield { ...result, page_count }
      }
      break

    case ContentType.PNG:
    case ContentType.JPEG:
      const result = await PDFConvert(
        { stream, size, type },
        options.image_resolution
      )
      yield { ...result, page_count: 1 }
      break
  }
}

export async function* TaskSplit(
  engine: PdfEngine,
  task: TaskSplitOptions,
  emit: TrackingCallback
): AsyncGenerator<TaskResult> {
  const { value: first, stream } = await Peek(
    SplitPages(
      engine,
      task.options,
      task.source,
      task.start_page,
      task.end_page,
      emit
    )
  )

  if (!first) return

  const total_pages = first.page_count
  const end_page = Math.min(task.end_page, total_pages)
  const total = end_page - task.start_page + 1

  void emit({
    name: TrackedTaskName.SPLIT_PDF,
    done: 0,
    total,
  })

  let page_number = task.start_page - 1

  for await (const { blob, trim_box, media_box, page_count } of stream) {
    page_number += 1

    const content = await GenerateFile(engine, [blob], ContentType.PDF, emit)

    yield {
      enum: TaskName.CONTENT_FILE,
      name: "page.pdf",
      content: {
        enum: VirtualFileType.SINGLE_PAGE_PDF,
        content,
        source: task.source,
        page: undefined,
        context: undefined,
        file_meta: {},
        page_number,
        page_count,
        trim_box: ConvertRectangle(task.options, trim_box),
        media_box: ConvertRectangle(task.options, media_box),
      },
    } as TaskResult

    void emit({
      name: TrackedTaskName.SPLIT_PDF,
      done: page_number - task.start_page + 1,
      total,
    })
  }
}
